import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Swords, Shield, Video, Hash, CheckCircle2, Trophy,
  ArrowRight, Users, Zap, Clock
} from 'lucide-react';

const GAME_MODES = [
  {
    id: 'commit_reveal',
    icon: Hash,
    title: 'Commit-Reveal Mode',
    subtitle: 'Cheat-Proof Digital',
    color: '#28A745',
    recommended: true,
    description: 'The most secure way to play. Both players secretly commit their move using cryptographic hashing, then reveal simultaneously. Impossible to cheat.',
    howItWorks: [
      'Choose your move (Rock, Paper, or Scissors)',
      'Your move is encrypted and locked',
      'Both players reveal at the same time',
      'Winner determined instantly'
    ],
    benefits: ['100% cheat-proof', 'No trust required', 'Instant verification']
  },
  {
    id: 'board_reveal',
    icon: Shield,
    title: 'Board Reveal Mode',
    subtitle: 'Write & Reveal',
    color: '#D4AF37',
    recommended: false,
    description: 'Classic style betting. Both players write their choice on a virtual board and reveal together. Simple, fast, and exciting.',
    howItWorks: [
      'Both players write their move',
      'Countdown timer starts',
      'Simultaneous reveal',
      'Winner takes the pot'
    ],
    benefits: ['Quick games', 'Easy to understand', 'Classic feel']
  },
  {
    id: 'webcam',
    icon: Video,
    title: 'Live Webcam Mode',
    subtitle: 'Face-to-Face',
    color: '#DC3545',
    recommended: false,
    description: 'The ultimate experience. Play face-to-face via webcam with real hand gestures. A moderator watches to ensure fair play.',
    howItWorks: [
      'Connect via webcam',
      'Moderator joins as referee',
      'Count down together (Rock, Paper, Scissors, SHOOT!)',
      'Moderator confirms winner'
    ],
    benefits: ['Real human interaction', 'Moderator verified', 'Most exciting']
  }
];

const RPSInfoPage = () => {
  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      <Header />

      {/* Hero */}
      <section className="py-16 px-4 text-center bg-gradient-to-b from-[#2D2D2D] to-[#1A1A1A]">
        <div className="flex justify-center gap-4 text-6xl mb-6">
          <span className="hover:scale-110 transition-transform">✊</span>
          <span className="hover:scale-110 transition-transform">✌️</span>
          <span className="hover:scale-110 transition-transform">🖐️</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-black uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
          Rock Paper Scissors <span className="text-[#D4AF37]">Live</span>
        </h1>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
          The world's first real-money Rock Paper Scissors platform. 
          Play against real opponents, stake real money, win real prizes.
        </p>
        
        {/* Stats */}
        <div className="flex justify-center gap-6 mt-8">
          <div className="stat-card">
            <Users className="h-6 w-6 mx-auto mb-2 text-[#D4AF37]" />
            <div className="text-xl font-bold text-[#D4AF37]">24+</div>
            <div className="text-xs text-gray-400">Online Now</div>
          </div>
          <div className="stat-card">
            <Zap className="h-6 w-6 mx-auto mb-2 text-[#28A745]" />
            <div className="text-xl font-bold text-[#28A745]">$5K+</div>
            <div className="text-xs text-gray-400">Won Today</div>
          </div>
          <div className="stat-card">
            <Trophy className="h-6 w-6 mx-auto mb-2 text-[#DC3545]" />
            <div className="text-xl font-bold text-[#DC3545]">100+</div>
            <div className="text-xs text-gray-400">Games Played</div>
          </div>
        </div>
      </section>

      {/* Game Modes */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-black uppercase text-white text-center mb-4" style={{ fontFamily: 'Outfit' }}>
            3 Ways to <span className="text-[#D4AF37]">Play</span>
          </h2>
          <p className="text-center text-gray-400 mb-12">Choose your preferred game mode</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {GAME_MODES.map((mode) => {
              const Icon = mode.icon;
              return (
                <div 
                  key={mode.id}
                  className={`card-dark card-dark-interactive relative ${mode.recommended ? 'border-[#28A745] border-2' : ''}`}
                  data-testid={`mode-${mode.id}`}
                >
                  {mode.recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="badge-green">Recommended</span>
                    </div>
                  )}
                  
                  <div 
                    className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${mode.color} 0%, ${mode.color}99 100%)` }}
                  >
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  
                  <h3 
                    className="text-xl font-bold text-center mb-1"
                    style={{ fontFamily: 'Outfit', color: mode.color }}
                  >
                    {mode.title}
                  </h3>
                  <p className="text-center text-gray-500 text-sm mb-4">{mode.subtitle}</p>
                  
                  <p className="text-gray-300 text-sm mb-4">{mode.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-[#D4AF37] mb-2">How it works:</h4>
                    <ol className="space-y-2">
                      {mode.howItWorks.map((step, idx) => (
                        <li key={idx} className="text-sm text-gray-400 flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-[#2D2D2D] flex items-center justify-center text-xs text-[#D4AF37] flex-shrink-0">
                            {idx + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {mode.benefits.map((benefit, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 rounded-full bg-[#2D2D2D] text-gray-300">
                        <CheckCircle2 className="h-3 w-3 inline mr-1 text-[#28A745]" />
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Rules */}
      <section className="py-12 px-4 bg-[#0F0F0F]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black uppercase text-white text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Quick <span className="text-[#D4AF37]">Rules</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card-dark">
              <h3 className="font-bold text-[#D4AF37] mb-3">Game Format</h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#28A745] mt-0.5" />
                  Best of 3 rounds wins the match
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#28A745] mt-0.5" />
                  Minimum stake: $1 or 1 Qbean
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#28A745] mt-0.5" />
                  Maximum stake: $1000
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#28A745] mt-0.5" />
                  10% platform fee on winnings
                </li>
              </ul>
            </div>
            
            <div className="card-dark">
              <h3 className="font-bold text-[#D4AF37] mb-3">Winning Rules</h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-center gap-2">
                  <span>✊</span> Rock beats <span>✌️</span> Scissors
                </li>
                <li className="flex items-center gap-2">
                  <span>✌️</span> Scissors beats <span>🖐️</span> Paper
                </li>
                <li className="flex items-center gap-2">
                  <span>🖐️</span> Paper beats <span>✊</span> Rock
                </li>
                <li className="flex items-center gap-2 text-gray-500">
                  Same move = Tie (replay round)
                </li>
              </ul>
            </div>
            
            <div className="card-dark">
              <h3 className="font-bold text-[#D4AF37] mb-3">Fair Play</h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-[#28A745] mt-0.5" />
                  Commit-reveal prevents cheating
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-[#28A745] mt-0.5" />
                  Moderators verify webcam games
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-[#28A745] mt-0.5" />
                  Disconnection = forfeit round
                </li>
              </ul>
            </div>
            
            <div className="card-dark">
              <h3 className="font-bold text-[#D4AF37] mb-3">Payouts</h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Zap className="h-4 w-4 text-[#D4AF37] mt-0.5" />
                  Winner receives 90% of total pot
                </li>
                <li className="flex items-start gap-2">
                  <Zap className="h-4 w-4 text-[#D4AF37] mt-0.5" />
                  Paid instantly to your wallet
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-[#D4AF37] mt-0.5" />
                  Withdraw anytime, anywhere
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#28A745] via-[#34D058] to-[#28A745]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-black uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            Ready to Play?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Challenge opponents worldwide. Win real money. It's that simple.
          </p>
          <Link to="/register">
            <Button className="bg-[#1A1A1A] text-[#28A745] px-10 py-5 text-lg font-bold uppercase rounded-lg shadow-lg hover:bg-[#2D2D2D] transition-all">
              Play Now <ArrowRight className="ml-2 h-5 w-5 inline" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RPSInfoPage;
